import { z } from 'zod';

export const commentStatusEnum = z.enum(['pending', 'approved', 'rejected', 'spam']);

export const listCommentsQuerySchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  perPage: z.coerce.number().int().positive().max(100).default(50),
  status: commentStatusEnum.optional(),
  // Post IDs are 13-digit timestamp strings (see apps/api/src/lib/post-id.ts),
  // NOT UUIDs — `uuid()` here was a pre-shipped typo that nobody hit until the
  // /comments page started passing the param. Loose digit range keeps it
  // future-proof past Y2286 (when timestamps grow to 14 digits).
  postId: z
    .string()
    .regex(/^\d{13,20}$/, 'invalid post id')
    .optional(),
  /** Exact-match filter for spam-pattern detection. IPv6 max length
   *  is 45 chars (8 groups of 4 hex + 7 colons + scope id buffer). */
  ipAddress: z.string().trim().min(1).max(45).optional(),
  /** ISO datetime — inclusive lower bound on createdAt. Used for the
   *  date-range preset chips ("วันนี้", "1 ชม. ล่าสุด" etc.) so
   *  moderators can scope a spam wave to its time window. */
  from: z.string().datetime().optional(),
  /** ISO datetime — exclusive upper bound on createdAt. */
  to: z.string().datetime().optional(),
  q: z.string().trim().min(1).max(255).optional(),
});

export type ListCommentsQuery = z.infer<typeof listCommentsQuerySchema>;

export const updateCommentStatusSchema = z.object({
  status: commentStatusEnum,
});

export type UpdateCommentStatusInput = z.infer<typeof updateCommentStatusSchema>;

/** Public submit — guest mode collects name/email; logged-in users skip those. */
export const submitCommentSchema = z.object({
  body: z.string().trim().min(1, 'กรุณาใส่ข้อความ').max(2000, 'ยาวเกินไป'),
  parentId: z.string().uuid().optional(),
  // Required when posting as guest; ignored when authenticated.
  authorName: z.string().trim().min(1).max(100).optional(),
  authorEmail: z.string().email().max(255).optional(),
});

export type SubmitCommentInput = z.infer<typeof submitCommentSchema>;

/** Admin staff reply — body only, server fills in postId/authorId/status. */
export const staffReplySchema = z.object({
  body: z.string().trim().min(1).max(2000),
  parentId: z.string().uuid().optional(),
});

export type StaffReplyInput = z.infer<typeof staffReplySchema>;
